import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { listModelIntegrations } from 'features/agents/api/agentsApi';
import { useCatalogModels } from 'shared/hooks/useCatalogModels';
import { EvalProviderDropdown, EvalModelDropdown } from 'features/evals/components/evalFormParts';
import ModelParamsPopover from 'shared/components/ui/ModelParamsPopover';
import { EmptyHint, Field } from 'features/agents/WorkflowEditor/fields/inputs';

/**
 * Which model an AI step runs on, and how it is asked to generate.
 *
 * The same three controls, from the same files, as the evaluator form: a
 * provider row with its brand mark, the models that provider offers (the
 * synced catalogue for the common ones, the deployments the team entered for
 * Azure and custom endpoints), and Params beside the model — temperature and
 * the rest behind one button, absent unless set, rather than a row of empty
 * boxes taking up the panel.
 *
 * An AI step always runs on the team's own key and costs no credits. Trodo's
 * own models are for Ask, which follows the team's Ask setup.
 *
 * value: { credential_id, model, ...generation params }
 */
/** The keys that are the choice of model, not how it generates. */
const CHOICE_KEYS = new Set(['credential_id', 'model']);

export default function AgentModelField({ value, onChange, readOnly = false }) {
  const model = value && typeof value === 'object' ? value : {};
  const { team } = useParams();
  const [integrations, setIntegrations] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listModelIntegrations()
      .then((rows) => { if (!cancelled) setIntegrations(rows || []); })
      .catch(() => { if (!cancelled) setIntegrations([]); });
    return () => { cancelled = true; };
  }, []);

  const rows = integrations || [];
  const selected = rows.find((r) => String(r.id) === String(model.credential_id)) || null;
  const modelsFor = useCatalogModels(null, rows);
  const models = selected ? modelsFor(selected) : [];

  // Params are the same object, minus the two keys that choose the model —
  // so a provider-specific key someone typed in JSON survives a slider drag.
  const params = Object.fromEntries(Object.entries(model).filter(([k]) => !CHOICE_KEYS.has(k)));
  const setParams = (next) => onChange({
    ...Object.fromEntries(Object.entries(model).filter(([k]) => CHOICE_KEYS.has(k))),
    ...next,
  });
  const set = (patch) => onChange({ ...model, ...patch });

  return (
    <div className="space-y-3">
      <Field label="Provider">
        {integrations === null ? (
          <EmptyHint>Loading</EmptyHint>
        ) : rows.length === 0 ? (
          <EmptyHint>
            <span>
              No provider connected yet.{' '}
              <Link to={`/${encodeURIComponent(team || '')}/settings/integrations`} className="text-[color:var(--text-primary)] underline">
                Connect one
              </Link>
              .
            </span>
          </EmptyHint>
        ) : (
          <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0 }}>
            <EvalProviderDropdown
              integrations={rows}
              value={model.credential_id ? String(model.credential_id) : ''}
              onChange={(id) => set({ credential_id: id || null, model: undefined })}
            />
          </fieldset>
        )}
      </Field>

      <Field label="Model">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0 }}>
              <EvalModelDropdown
                integration={selected}
                models={models}
                value={model.model || ''}
                onChange={(m) => set({ model: m || undefined })}
              />
            </fieldset>
          </div>
          <ModelParamsPopover value={params} onChange={setParams} disabled={readOnly || !selected} />
        </div>
      </Field>
    </div>
  );
}
